import { FC } from 'react';

import { CancelConfirmDialog } from './couponDetail/CancelConfirmDialog';
import { CancelSection } from './couponDetail/CancelSection';
import { CouponDetailHeader } from './couponDetail/CouponDetailHeader';
import { CouponTable } from './couponDetail/CouponTable';
import { DisposeConfirmDialog } from './couponDetail/DisposeConfirmDialog';
import { DisposeSection } from './couponDetail/DisposeSection';
import { RefundSection } from './couponDetail/RefundSection';
import { ReissueSection } from './couponDetail/ReissueSection';
import { RenewalSection } from './couponDetail/RenewalSection';
import { ResultBanners } from './couponDetail/ResultBanners';
import { CouponDetailModalProps } from './couponDetail/types';
import { useCouponDetailState } from './couponDetail/useCouponDetailState';

export type { CouponDetailModalProps };

export const CouponDetailModal: FC<CouponDetailModalProps> = (props) => {
  const { onClose } = props;
  const state = useCouponDetailState(props);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-[80vw] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <CouponDetailHeader
          stats={state.stats}
          orderId={props.orderId}
          order={props.order}
          previewCopied={state.previewCopied}
          setPreviewCopied={state.setPreviewCopied}
          onClose={onClose}
        />

        <ResultBanners
          cancelResult={state.cancelResult}
          additionalResult={state.additionalResult}
          disposeResult={state.disposeResult}
          refundResult={state.refundResult}
          renewalResult={state.renewalResult}
        />

        <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
          <RefundSection
            sortedCoupons={state.sortedCoupons}
            onRefund={state.handleRefund}
            isRefunding={state.isRefunding}
          />

          <RenewalSection
            sortedCoupons={state.sortedCoupons}
            onCreateRenewal={state.handleCreateRenewal}
            onDeleteRenewal={state.handleDeleteRenewal}
            isCreatingRenewal={state.isCreatingRenewal}
            isDeletingRenewal={state.isDeletingRenewal}
          />

          {state.canCancel && state.reissueStatsByItem.length > 0 && (
            <ReissueSection
              reissueStatsByItem={state.reissueStatsByItem}
              totalReissueRemaining={state.totalReissueRemaining}
              showAdditionalForm={state.showAdditionalForm}
              setShowAdditionalForm={state.setShowAdditionalForm}
              reissueGiftItemId={state.reissueGiftItemId}
              setReissueGiftItemId={state.setReissueGiftItemId}
              additionalCount={state.additionalCount}
              setAdditionalCount={state.setAdditionalCount}
              selectedReissueStat={state.selectedReissueStat}
              isCreatingAdditional={state.isCreatingAdditional}
              handleCreateAdditional={state.handleCreateAdditional}
            />
          )}

          {state.canCancel && (
            <DisposeSection
              disposeMode={state.disposeMode}
              setDisposeMode={state.setDisposeMode}
              disposeAction={state.disposeAction}
              setDisposeAction={state.setDisposeAction}
              disposeSelectedIds={state.disposeSelectedIds}
              setDisposeSelectedIds={state.setDisposeSelectedIds}
              setShowDisposeConfirm={state.setShowDisposeConfirm}
              disposableCoupons={state.disposableCoupons}
              toggleDisposeSelectAll={state.toggleDisposeSelectAll}
            />
          )}

          {state.canCancel && state.cancellableCoupons.length > 0 && (
            <CancelSection
              cancellableCoupons={state.cancellableCoupons}
              selectedIds={state.selectedIds}
              isFullCancel={state.isFullCancel}
              toggleSelectAll={state.toggleSelectAll}
              setShowCancelConfirm={state.setShowCancelConfirm}
            />
          )}

          <CouponTable
            sortedCoupons={state.sortedCoupons}
            giftItemEmbedMap={state.giftItemEmbedMap}
            canCancel={state.canCancel}
            hasCancellableCoupons={state.cancellableCoupons.length > 0}
            disposeMode={state.disposeMode}
            selectedIds={state.selectedIds}
            disposeSelectedIds={state.disposeSelectedIds}
            expandedShareHistory={state.expandedShareHistory}
            isPromoting={state.isPromoting}
            toggleSelect={state.toggleSelect}
            toggleDisposeSelect={state.toggleDisposeSelect}
            toggleShareHistory={state.toggleShareHistory}
            handlePromoteToReissued={state.handlePromoteToReissued}
          />
        </div>

        {state.showDisposeConfirm && (
          <DisposeConfirmDialog
            disposeAction={state.disposeAction}
            disposeSelectedIds={state.disposeSelectedIds}
            disposeReason={state.disposeReason}
            setDisposeReason={state.setDisposeReason}
            isDisposing={state.isDisposing}
            setShowDisposeConfirm={state.setShowDisposeConfirm}
            handleDispose={state.handleDispose}
          />
        )}

        {state.showCancelConfirm && (
          <CancelConfirmDialog
            isFullCancel={state.isFullCancel}
            hasDeliveryItems={state.hasDeliveryItems}
            selectedIds={state.selectedIds}
            cancelReason={state.cancelReason}
            setCancelReason={state.setCancelReason}
            isCancelling={state.isCancelling}
            setShowCancelConfirm={state.setShowCancelConfirm}
            handleCancelRequest={state.handleCancelRequest}
          />
        )}
      </div>
    </div>
  );
};
